import * as React from 'react';
// @ts-ignore
import nestedProperty from 'nested-property';
import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { adminInfoStoreQuery } from '../../graphql/queries';
import { Communicator } from '../../tools/Communication';
import { EditSection, ResponsiveTable } from '../Layout';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { LayoutStoreInstance } from '../../LayoutStore';
import { MerchantStore } from '../../views/MerchantComponents/MerchantModel';
import { useHistory } from 'react-router';
import {
  MerchantOrderItemStatus,
  MerchantOrderStatus,
  MerchantOrderType,
  Revision,
  MerchantOrderItem,
  StoreAssetOrderItem,
  MerchantOrderItemOption,
  isOnboarding,
  DPD_SUPPORTED_ORDER_TYPES,
} from './HandleStatusChange';
import {
  getCurrentDatePlusDays,
  getWorkingDayAfterNWorkingDays,
} from '@/utils/date';
import { ItemTable } from './ItemTable';
import { StoreAssetTable } from './StoreAssetTable';
import { orderTypeAllowsStoreAssets } from './storeAssetOrderTypes';
import {
  isContainerCountAllowedForOnboarding,
  SlackUrlObject,
  resolveDeliveryAddress,
  truncateAddressFields,
  buildDpdOrderDataItem,
} from './Utils';
import {
  checkShippingLabels,
  ErrorDataType,
  OrderDataType,
} from '../../utils/dpdApi';
import { testOrder } from './SubmitOrUpdateOrder';
import { calcPackageCount } from './HandleStatusChange';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { DeliveryAddressEditItem } from './DeliveryAddressEditItem';
import { OrderDetailsEditItem } from './OrderDetailsEditItem';
import { PurchasePrice } from './PurchasePrice';
import { submitOrUpdateOrder } from './SubmitOrUpdateOrder';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { ContactRole } from '../../graphql/generatedTypes';
import { DpdStatusHistory } from './DpdStatusHistory';
import { InternalTransactions } from './InternalTransactions';
import { ContentSection } from '../Layout/ContentSection';
import { extendArray } from '@/utils/arrayUtils';
import { Routes } from '@/utils/routes';
import { ContainerType } from '../../views/ContainerComponents/ContainerPage';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface StatusHistoryEntry {
  modifiedTimestamp: Date;
  status: number;
  userName: string;
}

export interface InternalTransactionsFromStore {
  storeName: string;
  containerTypeName: string;
  count: number;
  bookingDates: string[];
}

/**
 * A revision item as the Razor GET /MerchantOrder/Get returns it. `itemTypeId`
 * is set only on STORE_ASSET lines; container and untracked lines leave it null
 * and are identified by `typeId`.
 */
type LoadedRevisionItem = {
  typeId: number;
  itemTypeId?: number | null;
  status: number;
  amountInt: number;
  name?: string;
};

type EditMerchantOrderProps = {
  match: {
    params: {
      orderId: string | null;
      vytalId: string | null;
    };
  };
  title: string;
};

export function EditMerchantOrder({
  match: {
    params: { orderId, vytalId },
  },
  title,
}: EditMerchantOrderProps) {
  const viewer = useContext(ViewerContext);
  const canAccessPartnerData = CanAccessPartnerData(viewer);
  if (!canAccessPartnerData) {
    orderId = null;
  }
  const history = useHistory();
  useDocumentTitle('PageTitles:merchantOrder');
  const [
    earliestOrderExecutionDateByType,
    setEarliestOrderExecutionDateByType,
  ] = useState<{
    [id: number]: Date | null;
  }>({});
  const [earliestOrderExecutionDate, setEarliestOrderExecutionDate] =
    useState<Date>(getCurrentDatePlusDays(1));
  const [vytalStore, setVytalStore] = useState<MerchantStore | null>(null);
  const [storeUpdate, setStoreUpdate] = useState<any | undefined>(null);
  const [oldStoreUpdate, setOldStoreUpdate] = useState<any | undefined>(null);
  const [regions, setRegions] = useState([] as any[]);
  const [orderItemOptions, setOrderItemOptions] = useState<
    MerchantOrderItemOption[]
  >([]);
  const [defaultOrderItems, setDefaultOrderItems] = useState(
    [] as MerchantOrderItem[],
  );
  // STORE_ASSET lines live outside revision.items while editing: they all carry
  // typeId 0, so the (status, typeId) indexing the item table relies on cannot
  // tell them apart. Same split colugo makes between `containers`,
  // `untracked_items` and `items`. Flattened into revision.items on submit.
  const [storeAssetItems, setStoreAssetItems] = useState(
    [] as StoreAssetOrderItem[],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [ogRevisionStatus, setOgRevisionStatus] = useState(0);
  const [statusHistory, setStatusHistory] = useState(
    [] as StatusHistoryEntry[],
  );
  const [internalTransactionsFromStore, setInternalTransactionsFromStore] =
    useState([] as InternalTransactionsFromStore[]);
  const [slackUrls, setSlackUrls] = useState([] as SlackUrlObject[]);
  const [orderPromise, setOrderPromise] = useState({} as any);
  const [isFirstOrder, setIsFirstOrder] = useState<boolean | null>(null);
  const [dpdTrackingCode, setDpdTrackingCode] = useState('');
  const [dpdStatusCodes, setDpdStatusCodes] = useState<string | null>(null);
  const [wasUpdatedWithStoreIdOnce, setWasUpdatedWithStoreIdOnce] =
    useState(false);
  const [eventInfo, setEventInfo] = useState<{
    eventId: string | null;
    eventName: string | null;
    eventAccountId: string | null;
    eventAccountName: string | null;
  }>({
    eventId: null,
    eventName: null,
    eventAccountId: null,
    eventAccountName: null,
  });
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResults, setTestResults] = useState<{
    errors: ErrorDataType[];
    isLoading: boolean;
  }>({ errors: [], isLoading: false });
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [revision, setRevision] = useState({
    merchantOrderId: '00000000-0000-0000-0000-000000000000', // Guid empty
    status: MerchantOrderStatus.CreatedForReview as string | number,
    orderExecutionDate: getCurrentDatePlusDays(1),
    language: 'de',
    comment: '',
    internalComment: '',
    priority: 5,
    items: [...defaultOrderItems],
    type: MerchantOrderType.Resupply as string | number,
    revisionId: 0,
    deliveryAddress: {},
    parcelCount: 1,
    regionId: null,
  } as Revision);
  const [getPickpackStore, { data = { store: {} }, loading: loadingStore }] =
    useLazyQuery(adminInfoStoreQuery, {
      fetchPolicy: 'no-cache',
      variables: { id: null as null | string },
      onCompleted: (storeData) => {
        // @ts-ignore
        if (storeData) {
          // @ts-ignore
          storeData.store.delivery_address = resolveDeliveryAddress(
            storeData.store,
          );

          // if delivery address in revision is set overwrite with that
          // @ts-ignore
          storeData.store.delivery_address.company =
            revision.deliveryAddress.company ||
            // @ts-ignore
            storeData.store.delivery_address.company;
          // @ts-ignore
          storeData.store.delivery_address.first_name =
            revision.deliveryAddress.firstName ||
            // @ts-ignore
            storeData.store.delivery_address.first_name;
          // @ts-ignore
          storeData.store.delivery_address.last_name =
            revision.deliveryAddress.lastName ||
            // @ts-ignore
            storeData.store.delivery_address.last_name;
          // @ts-ignore
          storeData.store.delivery_address.streetname =
            revision.deliveryAddress.streetname ||
            // @ts-ignore
            storeData.store.delivery_address.streetname;
          // @ts-ignore
          storeData.store.delivery_address.streetno =
            revision.deliveryAddress.streetno ||
            // @ts-ignore
            storeData.store.delivery_address.streetno;
          // @ts-ignore
          storeData.store.delivery_address.zip =
            revision.deliveryAddress.zip ||
            // @ts-ignore
            storeData.store.delivery_address.zip;
          // @ts-ignore
          storeData.store.delivery_address.city =
            revision.deliveryAddress.city ||
            // @ts-ignore
            storeData.store.delivery_address.city;
          // @ts-ignore
          storeData.store.delivery_address.additional_info =
            revision.deliveryAddress.additionalInfo ||
            // @ts-ignore
            storeData.store.delivery_address.additional_info;
          // @ts-ignore
          storeData.store.delivery_address.email =
            revision.deliveryAddress.email ||
            // @ts-ignore
            storeData.store.delivery_address.email;

          storeData.store.delivery_address.phone_number =
            revision.deliveryAddress.phoneNumber ||
            // @ts-ignore
            storeData.store.delivery_address.phone_number;

          // @ts-ignore
          storeData.store.delivery_address.country =
            revision.deliveryAddress.country ||
            // @ts-ignore
            storeData.store.delivery_address.country;

          const isUSA = storeData.store.delivery_address.country === 'US';
          const isCanada = storeData.store.delivery_address.country === 'CA';

          if (isUSA || isCanada) {
            storeData.store.delivery_address.state =
              revision.deliveryAddress.state ||
              storeData.store.delivery_address.state ||
              storeData.store.address.state;
          } else {
            storeData.store.delivery_address.state = '';
          }

          resetStore(storeData.store);
          if (orderId == null) {
            orderPromise.then((data: any) => {
              setIsFirstOrder(data.orders.length === 0);
              updateRevision(
                'type',
                String(
                  data.orders.length > 0
                    ? MerchantOrderType.Resupply
                    : MerchantOrderType.OnboardingS,
                ),
                // @ts-ignore
                storeData.store.type,
              );
            });
          }
        }
      },
    });

  useEffect(() => {
    if (orderId == null) {
      getWorkingDayAfterNWorkingDays(0, getCurrentDatePlusDays(1)).then(
        (workingDay) => {
          setRevision({ ...revision, orderExecutionDate: workingDay });
        },
      );
    }
    Communicator.Get('/Merchant/GetRegions', {})
      .then(async (response) => {
        const regionData = (await response.json()) as any[];
        setRegions(regionData);
      })
      .catch((error) => {
        LayoutStoreInstance.setError('Failed to load region list: ' + error);
      });
    Communicator.Get('/Config/GetConfigValue', {
      key: 'MerchantOrderConfig',
    })
      .then((response) => response.json())
      .then((config) => {
        setSlackUrls(config.Regions);
      });
    Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: ContainerType[] }) => {
        if (data.containerTypes) {
          setContainerTypes(data.containerTypes);
        }
      });
  }, []);

  useEffect(() => {
    const storeId = vytalId || vytalStore?.id;
    const executeUpdate =
      orderItemOptions.length === 0 ||
      (storeId != null && !wasUpdatedWithStoreIdOnce);
    if (storeId != null) {
      setWasUpdatedWithStoreIdOnce(true);
    }
    if (!executeUpdate) {
      return;
    }

    var defaultItems: MerchantOrderItem[] = [];
    Communicator.Get('/api/3/MerchantOrder/GetOrderItemOptions', {
      storeId: vytalId || vytalStore?.id,
      orderId: orderId,
      // The options load before an existing order, so this is the editor's
      // default type. For a US store it limits the list to the container types
      // the store accepts; the lines of an existing order (orderId) stay.
      orderType: MerchantOrderType.Resupply,
    })
      .then((response) => response.json())
      .then((options: MerchantOrderItemOption[]) => {
        options = options.filter((opt) => opt.typeId != 0);
        setOrderItemOptions(options);
        defaultItems = options
          .map((i) => {
            return [
              {
                typeId: i.typeId,
                name: i.name,
                amount: '0',
                status: MerchantOrderItemStatus.Pickup,
              },
              {
                typeId: i.typeId,
                name: i.name,
                amount: '0',
                status: MerchantOrderItemStatus.Delivery,
              },
            ];
          })
          .reduce((acc, value) => acc.concat(value), []);
        setDefaultOrderItems(defaultItems);
      })
      .then(() => {
        loadStoreInfo(defaultItems);
      });
  }, [vytalId, vytalStore]);

  const orderPromiseWithOrder = new Promise<{ orders: any[] }>((resolve) => {
    resolve({ orders: ['order'] });
  });

  async function loadStoreInfo(defaultItems: MerchantOrderItem[]) {
    if (orderId != null) {
      try {
        const response = await Communicator.Get(`/MerchantOrder/Get`, {
          orderId,
        });
        if (!response.ok) {
          throw new Error('Bad Request');
        }
        const order = await response.json();
        if (
          Array.isArray(order.dpdTrackingCodes) &&
          order.dpdTrackingCodes.length > 0
        ) {
          const trackingCodes = order.dpdTrackingCodes as string[];
          setDpdTrackingCode(trackingCodes.join(', '));
        }
        setDpdStatusCodes(order.dpdStatusCodes);
        setInternalTransactionsFromStore(order.internalTransactionsFromStore);
        // For event-based orders, targetStoreId will be null
        vytalId = order.targetStoreId;
        // Store event information if this is an event-based order
        if (order.eventId) {
          setEventInfo({
            eventId: order.eventId,
            eventName: order.eventName || null,
            eventAccountId: order.eventAccountId || null,
            eventAccountName: order.eventAccountName || null,
          });
        }
        // Store-asset rows are not in defaultItems (which is built from
        // GetOrderItemOptions, i.e. container and untracked options only), so
        // they have to be pulled out before the typeId merge — otherwise the
        // findIndex below misses them and the amounts are silently dropped.
        const loadedItems: LoadedRevisionItem[] =
          order.currentRevision.items ?? [];
        setStoreAssetItems(
          loadedItems
            .filter((item) => item.itemTypeId != null && item.amountInt !== 0)
            .map((item) => ({
              itemTypeId: item.itemTypeId as number,
              name: item.name ?? String(item.itemTypeId),
              amount: item.amountInt,
            })),
        );

        const items = [...defaultItems];
        for (const item of order.currentRevision.items) {
          if (item.itemTypeId != null) {
            continue;
          }
          const index = items.findIndex(
            (i) => i.typeId === item.typeId && i.status == item.status,
          );
          if (index !== -1 && item.amountInt != 0) {
            items[index].amount = item.amountInt;
          }
        }
        order.currentRevision.items = items;
        order.currentRevision.orderExecutionDate = new Date(
          order.currentRevision.orderExecutionDate,
        );
        setOgRevisionStatus(order.currentRevision.status);
        setRevision(order.currentRevision);
        setStatusHistory(
          [...(order.revisions as Revision[])]
            .sort((a, b) => b.revisionId - a.revisionId)
            .map((r) => ({
              modifiedTimestamp: new Date(r.modifiedTimestamp),
              status: r.status as number,
              userName: r.createdByUserName as string,
            })),
        );
      } catch (e) {
        LayoutStoreInstance.setError('Failed to load order: ' + e);
      }
    }
    if (vytalId == null) {
      // Event-based orders don't have a store ID - skip store loading
      console.log('No store ID provided - this may be an event-based order');
      if (orderId !== null) {
        // For existing event orders, use a minimal store update object
        setStoreUpdate({
          delivery_address: revision.deliveryAddress || {},
          type: 'unknown',
          possible_merchant_order_types: [],
        });
      }
    } else {
      try {
        const response = await Communicator.Get('/Merchant/GetMerchantStore', {
          storeId: vytalId,
          includeIsKoziolPartner: true,
        });

        if (!response.ok) {
          throw new Error('Bad Request');
        }
        const store = await response.json();
        if (orderId === null) {
          setRevision((oldRevision) => ({
            ...oldRevision,
            items: defaultItems,
          }));
          let constraints = [];
          constraints.push({
            type: 'StoreIdConstraint',
            data: JSON.stringify({ storeId: store.id }),
          });
          const promise = canAccessPartnerData
            ? Communicator.PostData('MerchantOrder/SearchMerchantOrders', {
                offset: 0,
                limit: 1,
                constraints,
              }).then((response) => response.json())
            : orderPromiseWithOrder;
          setOrderPromise(promise);
        }
        getPickpackStore({
          variables: { id: store.externalPickpackId },
        });
        setVytalStore(store);
      } catch (e) {
        LayoutStoreInstance.setError('Failed to load vytal store: ' + e);
      }
    }
  }

  function resetStore(storeIn: any) {
    console.log('resetStore', storeIn.delivery_address);
    setStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;

    console.log('handleChange', { name, value });

    const truncatedValue = truncateAddressFields(value, name);

    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      nestedProperty.set(newStoreUpdate, name, truncatedValue);
      return newStoreUpdate;
    });
  }

  function updateRevision(
    attrName: string,
    attrValue: string | number,
    storeType?: string,
  ) {
    setRevision((oldRevision) => {
      const newRevision = { ...oldRevision };
      let formattedValue: string | Date | number = attrValue;
      if (attrName == 'orderExecutionDate') {
        formattedValue = new Date(attrValue);
        // selected day is on the weekend
        if ([0, 6].includes(formattedValue.getUTCDay())) {
          alert(i18n.t('MerchantOrder:alertNoOrderExecutionOnWeekends'));
          return newRevision;
        }
      }
      if (attrName == 'type') {
        const parsedType = parseInt(attrValue as string);
        if (
          parsedType !== MerchantOrderType.Both &&
          parsedType !== MerchantOrderType.Pickup
        ) {
          newRevision.items
            .filter(
              (i: MerchantOrderItem) =>
                i.status == MerchantOrderItemStatus.Pickup,
            )
            .filter((element: MerchantOrderItem) => {
              element.amount = '0';
            });
        }
        if (
          parsedType !== MerchantOrderType.Both &&
          parsedType !== MerchantOrderType.Resupply &&
          parsedType !== MerchantOrderType.WarehouseTransfer &&
          !isOnboarding(parsedType)
        ) {
          newRevision.items
            .filter(
              (i: MerchantOrderItem) =>
                i.status == MerchantOrderItemStatus.Delivery,
            )
            .forEach((element: MerchantOrderItem) => {
              element.amount = '0';
            });
        }
        // set pos package standard to 1 if onboarding
        if (isOnboarding(parsedType)) {
          if (
            !isContainerCountAllowedForOnboarding(
              parsedType,
              revision.items,
              storeType || storeUpdate.type,
            )
          ) {
            return oldRevision;
          } else {
            // canteen vs restaurant pos package
            const posPackageTypeId = vytalStore?.category?.id === 4 ? -11 : -4;
            const posPackage = newRevision.items.find(
              (i: MerchantOrderItem) =>
                i.status === MerchantOrderItemStatus.Delivery &&
                i.typeId === posPackageTypeId,
            );
            if (posPackage != null) {
              posPackage.amount = '1';
            }
          }
        }
        if (
          isOnboarding(parseInt(oldRevision.type as string)) &&
          !isOnboarding(parsedType)
        ) {
          const posPackage = newRevision.items.find(
            (i: MerchantOrderItem) =>
              i.status === MerchantOrderItemStatus.Delivery && i.typeId === -4,
          );
          if (posPackage != null) {
            posPackage.amount = '0';
          }
        }
        if (parsedType === MerchantOrderType.DPDPickup) {
          getWorkingDayAfterNWorkingDays(0, getCurrentDatePlusDays(1)).then(
            async (workingDay) => {
              if (
                workingDay < getCurrentDatePlusDays(2) &&
                new Date().getHours() >= 16
              ) {
                workingDay = await getWorkingDayAfterNWorkingDays(
                  1,
                  workingDay,
                );
              }
              if (workingDay > revision.orderExecutionDate) {
                setRevision((oldRevision) => ({
                  ...oldRevision,
                  orderExecutionDate: workingDay,
                }));
              }
            },
          );
        }
      }
      if (attrName.startsWith('packageWeights')) {
        const splitted = attrName.split('.');
        const index = parseInt(splitted[1]);
        const packageWeights = extendArray<string>(
          newRevision.packageWeights,
          index,
          '1',
        );
        packageWeights[index] = attrValue as string;
        newRevision.packageWeights = packageWeights;
        return newRevision;
      }
      (newRevision as any)[attrName] = formattedValue;
      return newRevision;
    });
  }

  function redirectToMerchantOrderOverview() {
    history.push(Routes.orders());
  }

  async function testDpdOrder() {
    if (!vytalStore) {
      LayoutStoreInstance.setError(
        i18n.t('MerchantOrder:alertAddressNotComplete'),
      );
      return;
    }

    const orderType = parseInt(revision.type as string, 10);
    if (!DPD_SUPPORTED_ORDER_TYPES.includes(orderType as any)) {
      LayoutStoreInstance.setError(
        i18n.t('MerchantOrder:alertUnsupportedType'),
      );
      return;
    }

    setTestResults({ errors: [], isLoading: true });
    setShowTestModal(true);

    try {
      // For new orders, use the resolved delivery address from the store
      // For existing orders, use the revision's delivery address
      const deliveryAddress =
        orderId == null ||
        !revision.deliveryAddress ||
        Object.keys(revision.deliveryAddress).length === 0
          ? resolveDeliveryAddress(storeUpdate)
          : revision.deliveryAddress;

      // Use existing testOrder function for validation
      const validationErrors = testOrder(
        storeUpdate,
        deliveryAddress,
        revision,
        vytalStore != null && vytalStore.appUsers.length !== 0,
      );

      if (validationErrors.length > 0) {
        const errorMessages = validationErrors
          .map((e: ErrorDataType) => e.errorMsgLong)
          .join('\n');
        LayoutStoreInstance.setError(errorMessages);
        setTestResults({ errors: validationErrors, isLoading: false });
        return;
      }

      const packageCount = calcPackageCount(revision, containerTypes);
      // Use extendArray to ensure we have default weights ('1') for all packages
      // This handles the case where packageWeights might be undefined for new orders
      const weights = extendArray(revision.packageWeights, packageCount, '1');

      // Use a test store ID and delivery note ID since this is just a test
      // For testing, use a shorter identifier that fits DPD's 35 character limit
      // Use store textId if available, otherwise use a shortened version of the store ID
      const testStoreId =
        (vytalStore as any)?.textId ||
        (vytalStore?.id ? vytalStore.id.substring(0, 8) : 'TEST');
      const testDeliveryNoteId = orderId ? '0' : '1'; // Use 1 for new orders (more realistic)

      const orderData: OrderDataType[] = [];
      for (let j = 1; j <= packageCount; j++) {
        orderData.push(
          buildDpdOrderDataItem(
            revision,
            deliveryAddress,
            testStoreId,
            testDeliveryNoteId,
            j,
            packageCount,
            weights[j - 1] || '1',
            0, // yourInternalID for test
          ),
        );
      }

      const errors = await checkShippingLabels(
        orderData,
        revision.orderExecutionDate,
      );
      setTestResults({ errors, isLoading: false });
    } catch (error) {
      LayoutStoreInstance.setError(
        `Failed to test order: ${error instanceof Error ? error.message : String(error)}`,
      );
      setTestResults({ errors: [], isLoading: false });
    }
  }

  function copyOrderLinkWithCompany() {
    if (!orderId) {
      LayoutStoreInstance.setError(
        i18n.t('MerchantOrder:orderNotSaved') ||
          'Order must be saved before copying link',
      );
      return;
    }

    // Match backend logic: use deliveryAddress.company, fallback to store name
    const companyName =
      revision.deliveryAddress?.company ||
      storeUpdate?.delivery_address?.company ||
      vytalStore?.name ||
      storeUpdate?.name ||
      '';

    if (!companyName) {
      LayoutStoreInstance.setError(
        i18n.t('MerchantOrder:companyNameMissing') ||
          'Company name is required',
      );
      return;
    }

    const baseUrl = window.location.origin;
    const orderUrl = `${baseUrl}${Routes.orderDetails(orderId)}`;
    const formattedString = `[${companyName}](${orderUrl})`;

    navigator.clipboard
      .writeText(formattedString)
      .then(() => {
        LayoutStoreInstance.setSuccess(
          i18n.t('MerchantOrder:linkCopied') ||
            'Order link copied to clipboard',
        );
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        LayoutStoreInstance.setError(
          i18n.t('MerchantOrder:copyFailed') || 'Failed to copy to clipboard',
        );
      });
  }

  async function submitOrder() {
    setIsLoading(true);
    const success = await submitOrUpdateOrder(
      revision,
      storeUpdate,
      slackUrls,
      orderItemOptions,
      regions,
      orderId,
      vytalStore?.country,
      vytalStore != null && vytalStore.appUsers.length !== 0,
      false,
      orderTypeAllowsStoreAssets(revision.type) ? storeAssetItems : [],
    );
    if (success) {
      if (canAccessPartnerData) {
        history.replace(Routes.orders());
      } else {
        if (vytalStore == null) {
          history.push(Routes.partnerMerchants());
        } else {
          const url =
            Routes.merchants(vytalStore.merchantId, vytalStore.id) +
            `?activeTab=${12}`;
          history.push(url);
        }
      }
    }
    setIsLoading(false);
  }

  if (storeUpdate == null) {
    return (
      <div>
        <p>{i18n.t('Common:loading')}</p>
      </div>
    );
  }

  // For event-based orders, vytalStore can be null
  const isEventBasedOrder = vytalStore == null && orderId != null;

  return (
    <EditSection
      title={(title || '') + i18n.t('Common:merchantOrder')}
      isLoading={isLoading}
      onSave={submitOrder}
      saveButtonText={
        orderId == null ? i18n.t('Common:submit') : i18n.t('Common:update')
      }
    >
      <div className="col-sm">
        {canAccessPartnerData && !isEventBasedOrder && (
          <div>
            <Button
              style={{ float: 'right', marginRight: '0.5rem' }}
              onClick={() => {
                history.push(
                  Routes.partnerBrandStore(
                    storeUpdate.brand?.id,
                    storeUpdate.id,
                  ),
                );
              }}
              disabled={storeUpdate == null || storeUpdate.brand == null}
            >
              {i18n.t('MerchantOrder:goToStore')}
            </Button>

            {orderId && (
              <Button
                style={{ float: 'right', marginRight: '0.5rem' }}
                onClick={copyOrderLinkWithCompany}
                color="secondary"
                title="Copy order link with company name"
              >
                Copy Order Link
              </Button>
            )}

            <Button
              style={{ float: 'right', marginRight: '0.5rem' }}
              onClick={redirectToMerchantOrderOverview}
              disabled={vytalStore == null}
            >
              {i18n.t('MerchantOrder:toOverview')}
            </Button>
            {canAccessPartnerData && (
              <Button
                style={{ float: 'right', marginRight: '0.5rem' }}
                onClick={testDpdOrder}
                disabled={!revision.deliveryAddress || !vytalStore}
                color="info"
              >
                Test DPD
              </Button>
            )}
          </div>
        )}
        {canAccessPartnerData && isEventBasedOrder && (
          <div className="alert alert-info">
            <strong>
              {i18n.t('MerchantOrder:eventBasedOrder') ||
                'This is an event-based order (no store associated)'}
            </strong>
            {eventInfo.eventId && (
              <div style={{ marginTop: '10px' }}>
                <div>
                  <strong>{i18n.t('Common:event')}:</strong>{' '}
                  <a href={Routes.eventDetail(eventInfo.eventId)}>
                    {eventInfo.eventName || eventInfo.eventId}
                  </a>
                </div>
                {eventInfo.eventAccountId && (
                  <div style={{ marginTop: '5px' }}>
                    <strong>{i18n.t('Common:eventAccount')}:</strong>{' '}
                    <a
                      href={Routes.eventAccountDetail(eventInfo.eventAccountId)}
                    >
                      {eventInfo.eventAccountName || eventInfo.eventAccountId}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <br style={{ clear: 'both' }} />
      <OrderDetailsEditItem
        updateRevision={updateRevision}
        ogRevisionStatus={ogRevisionStatus}
        revision={revision}
        orderId={orderId}
        earliestOrderExecutionDate={earliestOrderExecutionDate}
        dpdTrackingCode={dpdTrackingCode}
        canAccessPartnerData={canAccessPartnerData}
        isFirstOrder={isFirstOrder}
        storeType={storeUpdate.type || 'unknown'}
        availableReturnOptions={storeUpdate.possible_merchant_order_types || []}
      />
      {!isEventBasedOrder && (
        <>
          <DeliveryAddressEditItem
            handleChange={handleChange}
            storeUpdate={storeUpdate}
            orderExecutionDate={revision.orderExecutionDate}
            canAccessPartnerData={canAccessPartnerData}
            ogRevisionStatus={ogRevisionStatus}
            orderType={parseInt(revision.type as string, 10)}
            resetStore={resetStore}
          />
        </>
      )}
      <ItemTable
        orderItemOptions={orderItemOptions}
        revision={revision}
        earliestOrderExecutionDate={earliestOrderExecutionDate}
        setEarliestOrderExecutionDate={setEarliestOrderExecutionDate}
        earliestOrderExecutionDateByType={earliestOrderExecutionDateByType}
        setEarliestOrderExecutionDateByType={
          setEarliestOrderExecutionDateByType
        }
        setRevision={setRevision}
        orderId={orderId}
        ogRevisionStatus={ogRevisionStatus}
        storeType={storeUpdate.type || 'unknown'}
        canAccessPartnerData={canAccessPartnerData}
        showCurrentStock={true}
      />
      <StoreAssetTable
        orderType={revision.type}
        storeAssetItems={storeAssetItems}
        setStoreAssetItems={setStoreAssetItems}
        disabled={!canAccessPartnerData}
      />
      {canAccessPartnerData && (
        <PurchasePrice
          revision={revision}
          orderItemOptions={orderItemOptions}
        />
      )}
      {canAccessPartnerData && statusHistory.length > 0 ? (
        <ContentSection title={i18n.t('MerchantOrder:statusHistory')} locked>
          <ResponsiveTable className="mt-4">
            <thead>
              <tr>
                <th>{i18n.t('Common:name')}</th>
                <th>{i18n.t('MerchantOrder:modificationDate')}</th>
                <th>{i18n.t('Common:status')}</th>
              </tr>
            </thead>
            <tbody>
              {statusHistory.map((entry, index) => {
                return (
                  <tr key={index}>
                    <td>{entry.userName}</td>
                    <td>
                      {entry.modifiedTimestamp.toLocaleDateString(
                        i18n.language,
                        getDateFormatOptions(true),
                      )}
                    </td>
                    <td>{i18n.t(`MerchantOrder:Status:${entry.status}`)}</td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </ContentSection>
      ) : null}
      {canAccessPartnerData && (
        <DpdStatusHistory dpdStatusCodesJson={dpdStatusCodes} />
      )}
      {canAccessPartnerData && (
        <InternalTransactions
          internalTransactionsFromStore={internalTransactionsFromStore || []}
        />
      )}
      <Modal isOpen={showTestModal} toggle={() => setShowTestModal(false)}>
        <ModalHeader toggle={() => setShowTestModal(false)}>
          DPD Test Results
        </ModalHeader>
        <ModalBody>
          {testResults.isLoading ? (
            <p>{i18n.t('Common:loading')}</p>
          ) : testResults.errors.length === 0 ? (
            <div>
              <p style={{ color: 'green', fontWeight: 'bold' }}>
                ✓ Test Passed
              </p>
              <p>The order address is valid and accepted by DPD.</p>
            </div>
          ) : (
            <div>
              <p style={{ color: 'red', fontWeight: 'bold' }}>
                ✗ Test Failed ({testResults.errors.length} error
                {testResults.errors.length !== 1 ? 's' : ''})
              </p>
              <ul>
                {testResults.errors.map((error, index) => (
                  <li key={index} style={{ marginBottom: '0.5rem' }}>
                    <strong>{error.errorCode}:</strong>{' '}
                    {error.errorMsgLong || error.errorMsgShort}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowTestModal(false)}>
            {i18n.t('Common:close')}
          </Button>
        </ModalFooter>
      </Modal>
    </EditSection>
  );
}
